import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ImageRegenerator from '../../components/ImageRegenerator';
import OrdersList from '../../components/OrdersList';
import TransfersList from '../../components/TransfersList';
import IssueReportsList from '../../components/IssueReportsList';
import TaxonomyManager from '../../components/TaxonomyManager';
import CategorizationManager from '../../components/CategorizationManager';
import {
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  Build as ToolsIcon,
  Image as ImageIcon,
  Menu as MenuIcon,
  ShoppingCart as OrdersIcon,
  AccountBalance as TransfersIcon,
  BugReport as IssueReportsIcon,
  Category as CategoryIcon,
  AutoAwesome as CategorizationIcon,
} from '@mui/icons-material';

const TOOLS = [
  {
    id: 'regenerate-images',
    label: 'Regenerate Images',
    icon: <ImageIcon />,
    component: <ImageRegenerator />,
  },
  {
    id: 'orders',
    label: 'Orders',
    icon: <OrdersIcon />,
    component: <OrdersList />,
  },
  {
    id: 'transfers',
    label: 'Payment Transfers',
    icon: <TransfersIcon />,
    component: <TransfersList />,
  },
  {
    id: 'issue-reports',
    label: 'Reported Issues',
    icon: <IssueReportsIcon />,
    component: <IssueReportsList />,
  },
  {
    id: 'taxonomy',
    label: 'Card Taxonomy',
    icon: <CategoryIcon />,
    component: <TaxonomyManager />,
  },
  {
    id: 'categorization',
    label: 'Categorization',
    icon: <CategorizationIcon />,
    component: <CategorizationManager />,
  },
];

const AdminToolsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const toolFromUrl = searchParams.get('tool');
  const toolFromUrlNormalized =
    toolFromUrl === 'regenerate-thumbnails' ? 'regenerate-images' : toolFromUrl;
  const initialTool =
    toolFromUrlNormalized && TOOLS.find((t) => t.id === toolFromUrlNormalized)
      ? toolFromUrlNormalized
      : TOOLS[0].id;
  const [selectedTool, setSelectedTool] = useState(initialTool);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // Update selected tool when URL param changes
  useEffect(() => {
    const normalized = toolFromUrl === 'regenerate-thumbnails' ? 'regenerate-images' : toolFromUrl;
    if (normalized && TOOLS.find((t) => t.id === normalized)) {
      setSelectedTool(normalized);
    }
  }, [toolFromUrl]);

  const selectedToolData = TOOLS.find(tool => tool.id === selectedTool) || TOOLS[0];

  const handleToolSelect = (toolId) => {
    setSelectedTool(toolId);
    setSearchParams({ tool: toolId });
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const sidebarWidth = 280;

  return (
    <DashboardLayout title="ADMIN TOOLS">
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        {/* Left Sidebar Menu */}
        <Paper
          elevation={1}
          sx={{
            width: sidebarOpen ? sidebarWidth : 0,
            flexShrink: 0,
            overflow: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            display: isMobile && !sidebarOpen ? 'none' : 'block',
            position: isMobile ? 'fixed' : 'relative',
            top: isMobile ? 0 : 'auto',
            left: isMobile ? 0 : 'auto',
            height: isMobile ? '100vh' : 'auto',
            zIndex: isMobile ? 1300 : 'auto',
            backgroundColor: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ToolsIcon sx={{ mr: 1, fontSize: 28, color: 'primary.main' }} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                Admin Tools
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Administrative utilities for managing the platform.
            </Typography>
          </Box>

          <List sx={{ pt: 1 }}>
            {TOOLS.map((tool) => (
              <ListItem key={tool.id} disablePadding>
                <ListItemButton
                  selected={selectedTool === tool.id}
                  onClick={() => handleToolSelect(tool.id)}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    minHeight: 48,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.contrastText,
                      },
                    },
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: selectedTool === tool.id 
                        ? theme.palette.primary.contrastText 
                        : theme.palette.text.secondary,
                      minWidth: 40,
                    }}
                  >
                    {tool.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={tool.label}
                    primaryTypographyProps={{
                      fontWeight: selectedTool === tool.id ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            overflow: 'auto',
            backgroundColor: theme.palette.background.default,
            minWidth: 0, // Allows flexbox to shrink
          }}
        >
          {isMobile && !sidebarOpen && (
            <IconButton
              onClick={() => setSidebarOpen(true)}
              sx={{ mb: 2 }}
              color="primary"
            >
              <MenuIcon />
            </IconButton>
          )}
          {React.cloneElement(selectedToolData.component, { key: selectedTool })}
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default AdminToolsPage;
